// ── BCH P2P Exchange — Protocol Constants ─────────────────────────
//
// Architecture:
//   - Order book = Nostr addressable events (kind 30100) published by traders
//   - Trade chat  = Nostr encrypted DMs (kind 4) between maker & taker
//   - Settlement  = Direct on-chain BCH transaction between wallets
//   - Protocol fee = 0.5% automatically included in every trade tx output
//   - Fidelity bonds = both parties lock BCH as collateral during trade
//   - No custodian, no arbitrator, no server — pure Nostr + BCH
//
// Revenue model:
//   Every completed trade sends 0.5% of the BCH leg to PLATFORM_FEE_ADDRESS
//   baked directly into the send transaction. No human controls this.
//
// Bond System:
//   Both maker and taker lock a fidelity bond (% of trade value) by sending
//   BCH to a trade-specific bond address. Bonds are published as Nostr events
//   with the UTXO txid as proof. When both parties confirm completion, bonds
//   are returned. If one party cheats, they lose reputation (the bond UTXO
//   serves as on-chain evidence of the trade). This creates economic incentive
//   to complete trades honestly — cheating costs you real BCH + your reputation.

import { PLATFORM_FEE_ADDRESS } from './platform';

export const EXCHANGE_TAG = 'bch-p2p-exchange';
export const OFFER_KIND = 30100;          // Addressable offer event (replaceable)
export const TRADE_KIND = 30101;          // Addressable trade event (tracks trade state)
export const TRADE_MSG_KIND = 14;         // NIP-24 sealed DM for trade chat

export const PROTOCOL_FEE_PERCENT = 0.005;  // 0.5%
export const MIN_PROTOCOL_FEE_SATS = 546;   // BCH dust limit

// ── Bond System ───────────────────────────────────────────────────
export const DEFAULT_BOND_PERCENT = 5;    // 5% of trade amount
export const MIN_BOND_SATS = 1000;        // minimum bond = 1000 sats

export type OfferSide = 'buy' | 'sell';     // buy BCH / sell BCH
export type PaymentMethod = 'paypal' | 'venmo' | 'cashapp' | 'zelle' | 'revolut' | 'bank' | 'cash' | 'other';
export type TradeStatus = 'open' | 'in_progress' | 'completed' | 'cancelled' | 'disputed';
export type BondStatus = 'none' | 'pending' | 'locked' | 'released' | 'forfeited';

export interface Offer {
  // From Nostr event
  eventId: string;
  pubkey: string;
  dTag: string;
  createdAt: number;

  // From content JSON
  side: OfferSide;           // 'buy' = I want BCH, 'sell' = I have BCH
  minSats: number;           // minimum trade size in sats
  maxSats: number;           // maximum trade size in sats
  fiatCurrency: string;      // 'USD', 'EUR', etc.
  priceType: 'market' | 'fixed';
  premiumPct: number;        // e.g. 2 = 2% above market, -1 = 1% below
  fixedRateFiat?: number;    // only if priceType === 'fixed'
  paymentMethods: PaymentMethod[];
  paymentDetails?: string;   // e.g. "PayPal: user@email.com"
  terms?: string;            // trader's custom terms
  status: TradeStatus;
  tradeCount?: number;       // completed trades (reputation)
  bondPercent: number;        // bond requirement (% of trade amount), 0 = no bond
}

// ── Trade tracking (kind 30101) ───────────────────────────────────
export interface Trade {
  eventId: string;
  dTag: string;
  offerDTag: string;
  makerPubkey: string;       // who posted the offer
  takerPubkey: string;       // who accepted the offer
  tradeSats: number;         // agreed trade amount
  bondSats: number;          // bond amount each party deposits
  makerBondTxid?: string;    // on-chain proof of maker's bond deposit
  takerBondTxid?: string;    // on-chain proof of taker's bond deposit
  makerConfirmed: boolean;   // maker says trade completed
  takerConfirmed: boolean;   // taker says trade completed
  status: TradeStatus;
  createdAt: number;
}

/** Calculate required bond in sats for a given trade amount and bond percent */
export function calcBondSats(tradeSats: number, bondPercent: number): number {
  if (bondPercent <= 0) return 0;
  return Math.max(Math.ceil(tradeSats * (bondPercent / 100)), MIN_BOND_SATS);
}

/** Parse a raw Nostr event into a Trade */
export function parseTrade(ev: any): Trade | null {
  try {
    const d = JSON.parse(ev.content);
    if (!d.makerPubkey || !d.takerPubkey) return null;
    return {
      eventId: ev.id,
      dTag: ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || ev.id,
      offerDTag: d.offerDTag || '',
      makerPubkey: d.makerPubkey,
      takerPubkey: d.takerPubkey,
      tradeSats: d.tradeSats || 0,
      bondSats: d.bondSats || 0,
      makerBondTxid: d.makerBondTxid,
      takerBondTxid: d.takerBondTxid,
      makerConfirmed: !!d.makerConfirmed,
      takerConfirmed: !!d.takerConfirmed,
      status: d.status || 'open',
      createdAt: ev.created_at,
    };
  } catch { return null; }
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  paypal: 'PayPal',
  venmo: 'Venmo',
  cashapp: 'Cash App',
  zelle: 'Zelle',
  revolut: 'Revolut',
  bank: 'Bank Transfer',
  cash: 'Cash in Person',
  other: 'Other',
};

export const FIAT_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'BRL', 'MXN', 'INR', 'NGN'];

/** Calculate protocol fee for a BCH trade amount */
export function calcProtocolFee(sats: number): number {
  return Math.max(Math.ceil(sats * PROTOCOL_FEE_PERCENT), MIN_PROTOCOL_FEE_SATS);
}

/** Parse a raw Nostr event into an Offer */
export function parseOffer(ev: any): Offer | null {
  try {
    const d = JSON.parse(ev.content);
    if (!d.side || !d.minSats || !d.maxSats) return null;
    return {
      eventId: ev.id,
      pubkey: ev.pubkey,
      dTag: ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || ev.id,
      createdAt: ev.created_at,
      side: d.side,
      minSats: d.minSats,
      maxSats: d.maxSats,
      fiatCurrency: d.fiatCurrency || 'USD',
      priceType: d.priceType || 'market',
      premiumPct: d.premiumPct ?? 0,
      fixedRateFiat: d.fixedRateFiat,
      paymentMethods: d.paymentMethods || [],
      paymentDetails: d.paymentDetails,
      terms: d.terms,
      status: d.status || 'open',
      tradeCount: d.tradeCount || 0,
      bondPercent: d.bondPercent ?? DEFAULT_BOND_PERCENT,
    };
  } catch {
    return null;
  }
}

/** Calculate the BCH price for a trade given market price and premium */
export function calcTradePrice(marketPriceFiat: number, premiumPct: number): number {
  return marketPriceFiat * (1 + premiumPct / 100);
}

/** Format sats as a human-readable BCH amount */
export function formatBCH(sats: number): string {
  if (sats >= 1e8) return `${(sats / 1e8).toFixed(4)} BCH`;
  if (sats >= 1e6) return `${(sats / 1e6).toFixed(2)}M sats`;
  if (sats >= 1e3) return `${(sats / 1e3).toFixed(1)}k sats`;
  return `${sats.toLocaleString()} sats`;
}

export { PLATFORM_FEE_ADDRESS };

